import {useQuery} from '@tanstack/react-query'
import {Plus} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {formatDateOnly} from '@/lib/date-utils'
import {ConstraintSeverityBadge, ConstraintType} from '@/types/constraint'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {ConstraintControllerListEndpoint} from '@/types/endpoint-contracts'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {Icon} from '@/components/shared/Icon'

export function SchemaPolicies() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.schemaPolicies.pageTitle)
    const navigate = useNavigate()
    const {hasPermission} = useAuth()
    const canCreate = hasPermission(permissions.constraints.create)

    const {
        page, pageSize,
        setPage, setPageSize, pageSizeOptions,
    } = useServerPagination({
        endpoint: ConstraintControllerListEndpoint,
        sortable: false,
        defaultSortBy: ConstraintControllerListEndpoint.sort.NAME,
    })

    const {data: policiesData, isLoading} = useQuery({
        queryKey: ['schema-policies', page, pageSize],
        queryFn: () => services.constraints.listSummary({page, size: pageSize}),
    })

    const policies = policiesData?.content || []
    const totalElements = policiesData?.totalElements || 0
    const totalPages = policiesData?.totalPages || 0

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.constraint}
            title={i18n.schemaPolicies.allPolicies}
            count={totalElements}
            description={i18n.schemaPolicies.policiesCount({count: totalElements})}
            data-testid="schema-policies-list"
            actions={canCreate ? (
                <Button onClick={() => navigate(routes.schema.policies.create())} data-testid="btn-create-policy">
                    <Icon icon={Plus} size="md" className="mr-2"/>
                    {i18n.schemaPolicies.createPolicy}
                </Button>
            ) : undefined}
            pagination={totalPages > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalPages={totalPages}
                    totalItems={totalElements}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.schemaPolicies.name}</TableHead>
                        <TableHead data-testid="col-type">{i18n.schemaPolicies.type}</TableHead>
                        <TableHead data-testid="col-severity">{i18n.schemaPolicies.severity}</TableHead>
                        <TableHead data-testid="col-enabled">{i18n.schemaPolicies.enabled}</TableHead>
                        <TableHead data-testid="col-violations">{i18n.schemaPolicies.openViolations}</TableHead>
                        <TableHead data-testid="col-last-reviewed">{i18n.schemaPolicies.lastReviewed}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading && (
                        <TableEmptyState
                            colSpan={6}
                            hasFilters={false}
                            matchMessage={i18n.schemaPolicies.loadingPolicies}
                            emptyMessage={i18n.schemaPolicies.loadingPolicies}
                        />
                    )}
                    {!isLoading && policies.map((policy) => (
                        <ClickableTableRow
                            key={policy.id}
                            to={routes.schema.policies.edit(policy.id)}
                            data-testid={`policy-row-${policy.id}`}
                        >
                            <TableCell className="font-medium">
                                <TextLink
                                    to={routes.schema.policies.edit(policy.id)}
                                    testId={`link-policy-${policy.id}`}
                                    stopPropagation
                                >
                                    {policy.name}
                                </TextLink>
                            </TableCell>
                            <TableCell>
                                <Badge variant={BadgeVariant.OUTLINE}>
                                    {policy.type === ConstraintType.CEL ? i18n.schemaPolicies.typeDsl : i18n.schemaPolicies.typeNativeCode}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant={ConstraintSeverityBadge.fromCode(policy.severity).variant}>
                                    {policy.severity}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant={policy.enabled ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                                    {policy.enabled ? i18n.common.active : i18n.common.inactive}
                                </Badge>
                            </TableCell>
                            <TableCell data-testid={`policy-violations-${policy.id}`}>
                                {policy.evalStatus === 'STALE'
                                    ? <Badge variant={BadgeVariant.WARNING}>{i18n.schemaPolicies.evalStatusStale}</Badge>
                                    : policy.openViolationsCount}
                            </TableCell>
                            <TableCell>
                                {policy.lastReviewedAt
                                    ? formatDateOnly(policy.lastReviewedAt)
                                    : i18n.schemaPolicies.lastReviewedNever}
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {!isLoading && policies.length === 0 && (
                        <TableEmptyState
                            colSpan={6}
                            hasFilters={false}
                            matchMessage={i18n.schemaPolicies.noPoliciesFound}
                            emptyMessage={i18n.schemaPolicies.noPoliciesFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
